import React from "react";
import moment from "moment";
import { useMutation, useSubscription } from "@apollo/react-hooks";
import {
  MY_LEAVE_USERS_TO_EXAMINER_DASHBOARD,
  MY_LEAVE_USERS_WITH_REQUEST,
  MY_LEAVE_REQUESTS_SUBSCRIPTION,
} from "my-leave/graphql";

export type UseMyLeaveUsersWithRequestParams = {
  onlyRequested: boolean;
  roleFilters: Record<string, any>;
  tableData?: {
    sortField?: string;
    sortDirection?: string;
    page?: number;
    pageSize?: number;
    searchField?: string;
    searchText?: string;
  };
  showRemoteWorks?: boolean;
  startDate: Date | string;
  endDate: Date | string;
  onCompleted: (result: any[]) => void;
  dashboard?: boolean;
  role: string;
};

export const useMyLeaveUsersWithRequest = ({
  onlyRequested,
  roleFilters,
  tableData,
  showRemoteWorks,
  startDate: rawStartDate,
  endDate: rawEndDate,
  onCompleted,
  dashboard,
  role,
}: UseMyLeaveUsersWithRequestParams) => {
  let startDate = rawStartDate;
  if (role === "examiner") {
    startDate = moment(rawStartDate).subtract(1, "month").startOf("month").toDate();
  }
  const endDate = rawEndDate;

  const queryParams = React.useMemo(() => {
    const params: {
      roleFilters: Record<string, any>;
      onlyRequested: boolean;
      sortField?: string;
      sortDirection?: string;
      showRemoteWorks?: boolean;
      role: string;
      startDate: Date | string;
      endDate: Date | string;
      page?: number;
      pageSize?: number;
      searchField?: string;
      searchText?: string;
    } = {
      roleFilters: { ...roleFilters },
      onlyRequested,
      sortField: tableData?.sortField,
      sortDirection: tableData?.sortDirection,
      showRemoteWorks,
      role,
      startDate,
      endDate,
      page: tableData?.page,
      pageSize: tableData?.pageSize,
    };

    if (tableData?.searchField && tableData?.searchText) {
      params.searchField = tableData.searchField;
      params.searchText = tableData.searchText;
    }

    return params;
  }, [
    roleFilters,
    onlyRequested,
    tableData?.sortField,
    tableData?.sortDirection,
    showRemoteWorks,
    role,
    startDate,
    endDate,
    tableData?.page,
    tableData?.pageSize,
    tableData?.searchField,
    tableData?.searchText,
  ]);

  const QUERY = dashboard
    ? MY_LEAVE_USERS_TO_EXAMINER_DASHBOARD
    : MY_LEAVE_USERS_WITH_REQUEST;

  const [runQuery, { data, loading, error }] = useMutation(QUERY);

  const loadQuery = React.useCallback(async () => {
    const mutationResult = await runQuery({
      variables: queryParams,
    });
    let result: any[] = [];
    if (dashboard) {
      result = mutationResult.data?.usersToExaminerDashboard ?? [];
    } else {
      result = mutationResult.data?.usersWithRequest ?? [];
    }
    onCompleted(result);
    return mutationResult;
  }, [runQuery, queryParams, onCompleted, dashboard]);

  const { error: subscriptionError } = useSubscription(
    MY_LEAVE_REQUESTS_SUBSCRIPTION,
    {
      onData: () => {
        loadQuery();
      },
    },
  );

  if (subscriptionError) {
    console.log({
      component: "useMyLeaveUsersWithRequest",
      errorDescription:
        "Error occured when subscribe data with MY_LEAVE_REQUESTS_SUBSCRIPTION query",
      error: subscriptionError,
    });
  }

  let result = dashboard
    ? data?.usersToExaminerDashboard ?? []
    : data?.usersWithRequest ?? [];

  return {
    data: result,
    loading,
    error,
    loadQuery,
  };
};

export default useMyLeaveUsersWithRequest;
